'use client'

import { Bot } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { relativeTime } from '../agent-activity/relative-time'
import { cn } from '../cn'
import { CopyButton } from './copy-button'

interface AgentChatMessageBubbleProps {
  role: 'assistant' | 'user' | 'system'
  content: string
  timestamp: string
  agentAvatar?: string | null
  /** Optional override for the right-aligned author label on user rows. */
  userLabel?: string
  /** [FACTORY-111] Optional attachment thumbnails shown above a user message.
   *  Absent for the factory chat (so its rows are unchanged); Workspaces maps
   *  its attachments here. */
  imageUrls?: string[]
}

/** Shared markdown body for both chat bubbles. Raw HTML is intentionally NOT
 *  enabled (no `rehype-raw`), so user-authored markdown renders as text and
 *  structure only — it adds no HTML/script-injection surface. */
function MarkdownBody({ content }: { content: string }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
}

/**
 * [FACTORY-116] The user bubble sits on `bg-primary text-primary-foreground` (a
 * saturated surface), so the default @tailwindcss/typography palette — grey
 * body/headings, blue links, light-grey code background — is illegible on it.
 * Pin every prose color to the bubble's own foreground (`currentColor`) and
 * give inline/fenced code a subtle translucent chip that stays readable on the
 * primary color. No `dark:prose-invert` here: the bubble is `bg-primary` in
 * both themes, so `currentColor` already tracks the correct foreground.
 */
const userBubbleProseClassName = [
  // base typography, matching the assistant bubble (minus dark:prose-invert)
  'prose prose-sm max-w-none wrap-anywhere prose-pre:my-2 prose-p:my-1.5',
  // body, headings, emphasis, links, inline + fenced code text
  '[--tw-prose-body:currentColor] [--tw-prose-headings:currentColor] [--tw-prose-bold:currentColor]',
  '[--tw-prose-links:currentColor] [--tw-prose-code:currentColor] [--tw-prose-pre-code:currentColor]',
  // blockquotes, captions, list markers, rules, table borders
  '[--tw-prose-quotes:currentColor] [--tw-prose-quote-borders:currentColor] [--tw-prose-captions:currentColor]',
  '[--tw-prose-bullets:currentColor] [--tw-prose-counters:currentColor] [--tw-prose-hr:currentColor]',
  '[--tw-prose-th-borders:currentColor] [--tw-prose-td-borders:currentColor]',
  // legible translucent surface for code instead of the default light-grey bg
  'prose-code:bg-primary-foreground/15 prose-pre:bg-primary-foreground/15',
  // a fenced block's <pre> already carries the /15 tint, and the prose-code
  // utility (higher specificity than the plugin's `pre code { transparent }`)
  // would otherwise re-tint the nested <code> — re-zero it so code fences show
  // a single layer, not two stacked ones.
  '[&_pre_code]:bg-transparent',
].join(' ')

/**
 * One row of the chat list. Renders three visual variants:
 *   - assistant: avatar + bubble on the left, markdown body
 *   - user:      bubble on the right, markdown body (+ optional image thumbnails)
 *   - system:    compact muted line, single-row (status / PR / preview)
 */
export function AgentChatMessageBubble({
  role,
  content,
  timestamp,
  agentAvatar,
  userLabel,
  imageUrls,
}: AgentChatMessageBubbleProps) {
  if (role === 'system') {
    return (
      <div className="flex items-center gap-2 px-4 py-1 text-xs text-muted-foreground">
        <span className="font-medium">{content}</span>
        <span aria-hidden>·</span>
        <span>{relativeTime(timestamp)}</span>
      </div>
    )
  }
  const isUser = role === 'user'
  return (
    <div className={cn('flex items-start gap-2.5 px-4 py-2', isUser && 'justify-end')}>
      {!isUser && (
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-sm">
          {agentAvatar ?? <Bot className="h-4 w-4" />}
        </div>
      )}
      <div className={cn('group min-w-0 max-w-[80%] space-y-1', isUser && 'items-end text-right')}>
        <div
          className={cn(
            'inline-block rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
            isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
          )}>
          {isUser ? (
            <>
              {imageUrls && imageUrls.length > 0 ? (
                <div className="mb-1.5 flex flex-wrap justify-end gap-1.5">
                  {imageUrls.map((url, i) => (
                    <img
                      key={`${url}-${i}`}
                      src={url}
                      alt=""
                      className="max-h-40 max-w-[12rem] rounded-md object-cover"
                    />
                  ))}
                </div>
              ) : null}
              {/* `text-left` so markdown (lists, tables, code) reads naturally
                  inside the right-anchored bubble; the bubble itself stays on
                  the right via the row's `justify-end`. */}
              <div className={cn('text-left', userBubbleProseClassName)}>
                <MarkdownBody content={content} />
              </div>
            </>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none wrap-anywhere prose-pre:my-2 prose-p:my-1.5">
              <MarkdownBody content={content} />
            </div>
          )}
        </div>
        {/* [FACTORY-98] Copy the raw message source (markdown for agents, plain
            text for the operator). Reserved in the footer so revealing it on
            hover/focus causes no layout shift. */}
        <div className={cn('flex items-center gap-1.5 text-[11px] text-muted-foreground', isUser && 'justify-end')}>
          <span>
            {isUser ? `${userLabel ?? 'You'} · ` : ''}
            {relativeTime(timestamp)}
          </span>
          <CopyButton
            text={content}
            label="Copy message"
            className="opacity-0 transition-opacity group-hover:opacity-100"
          />
        </div>
      </div>
    </div>
  )
}
